<script setup lang="ts">
import { PriceOverrideStatus } from "@classes-service-types/index";
import { useToggle } from "@vueuse/core";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import Column from "primevue/column";
import DataTable, { type DataTableRowClickEvent } from "primevue/datatable";
import Divider from "primevue/divider";
import Dropdown from "primevue/dropdown";
import OverlayPanel from "primevue/overlaypanel";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { filtersPresets } from "@/components/OperationsApp/components/Filters/styles.ts";
import DetailModal from "@/components/OperationsApp/components/Modals/DetailModal/index.vue";
import PriceOverrideBulkUpdateModal from "@/components/OperationsApp/components/Modals/PriceOverrideBulkUpdateModal/index.vue";
import ApprovalStatus from "@/components/OperationsApp/components/PriceOverrideTable/ApprovalStatus.vue";
import { useShowColumns } from "@/components/OperationsApp/components/PriceOverrideTable/useShowColumns.ts";
import { useFilters } from "@/components/OperationsApp/composables/useFilters.ts";
import { useProductTypeReasonFilters } from "@/components/OperationsApp/composables/useProductTypeReasonFilters.ts";
import { useQFilter } from "@/components/OperationsApp/composables/useQFilter.ts";
import { useSortFilters } from "@/components/OperationsApp/composables/useSortFilters.ts";
import { useUpdateDecisionDates } from "@/components/OperationsApp/composables/useUpdateDecisionDates.ts";
import { getPriceDifference } from "@/components/OperationsApp/utils.ts";
import { useGetPriceOverrides } from "@/providers/priceOverrides/useGetPriceOverrides.ts";
import { formatDateUtc } from "@/util/date";
import { formattedPriceFromCents } from "@/util/formatter";
import {
  APPROVED_HIDDEN_COLUMNS,
  COLUMN_OPTIONS,
  COLUMNS_NOT_TO_ENABLE_AT_LOAD,
  DEFAULT_HIDDEN_COLUMNS,
  NEEDS_REVIEW_HIDDEN_COLUMNS,
  REJECTED_HIDDEN_COLUMNS,
  REVERTED_HIDDEN_COLUMNS,
  ROWS_PER_PAGE_DROPDOWN_OPTIONS,
  ROWS_PER_PAGE_OPTIONS,
} from "./constants.ts";
import { priceOverrideTablePresets } from "./styles.ts";
import type { Props } from "./types.ts";

defineOptions({
  name: "PriceOverrideTable",
});

const props = withDefaults(defineProps<Props>(), {});

const pageLimit = ref(ROWS_PER_PAGE_OPTIONS[1]);
const pageSkip = ref(0);
const currentPage = ref(1);
const currentFirst = ref(0);

const selectedRows = ref([]);

const columnSelectorPanel = ref();

const router = useRouter();
const toast = useToast();

const { sortOrder, sortBy } = useSortFilters();
const { q } = useQFilter();
const { decisionDateEnd, decisionDateStart } = useUpdateDecisionDates();
const { libraryId, reasonId } = useProductTypeReasonFilters();
const { status, session, city, isAllStatus, orderStatus } = useFilters();

const { displayableColumns, canDisplayColumn, columnOptions, setHiddenColumns } = useShowColumns(
  COLUMN_OPTIONS,
  COLUMNS_NOT_TO_ENABLE_AT_LOAD,
);

const {
  isLoading,
  data: priceOverridesResponse,
  isError,
  refetch,
  isRefetching,
} = useGetPriceOverrides(
  status,
  session,
  city,
  libraryId,
  reasonId,
  orderStatus,
  decisionDateStart,
  decisionDateEnd,
  q,
  pageLimit,
  pageSkip,
  sortBy,
  sortOrder,
  true,
  {
    refetchInterval: false,
    refetchOnWindowFocus: false,
    throwOnError: (error) => {
      toast.add({
        severity: "error",
        summary: "Price Overrides",
        detail: `Failed to get Price Overrides please refresh page. ${error.message}`,
        group: "br",
        life: 5000,
      });
      return false;
    },
  },
);

const toggleColumnSelectorPanel = (event) => {
  columnSelectorPanel.value?.toggle(event);
};

const showViewDetails = computed(() => {
  return (
    (!isAllStatus.value && status.value?.includes(PriceOverrideStatus.Approved)) ||
    status.value?.includes(PriceOverrideStatus.Rejected)
  );
});

const showApprovedDate = computed(() => {
  return !isAllStatus.value && status.value?.includes(PriceOverrideStatus.Approved);
});

const showRequestedDate = computed(() => {
  return !isAllStatus.value && status.value?.includes(PriceOverrideStatus.NeedsReview);
});

const showRejectedDate = computed(() => {
  return !isAllStatus.value && status.value?.includes(PriceOverrideStatus.Rejected);
});

const enableBulkEdit = computed(() => {
  return status.value?.length === 1 && status.value?.includes(PriceOverrideStatus.NeedsReview);
});

const [showBulkEditModal, toggleShowBulkEditModal] = useToggle(false);

const bulkEdit = () => {
  if (selectedRows.value?.length > 0) {
    toggleShowBulkEditModal(true);
  } else {
    toast.add({
      severity: "warn",
      summary: "Bulk Edit",
      detail: "Please select at least one item to bulk edit",
      group: "br",
      life: 5000,
    });
  }
};

const pageCount = computed(() => Math.ceil((priceOverridesResponse?.value?.data?.count || 0) / pageLimit.value));
const pageDropDownOptions = computed(() =>
  Array.from({ length: pageCount.value }, (_, i) => ({ key: `${i + 1}`, value: i + 1 })),
);

const onRowClicked = (event: DataTableRowClickEvent) => {
  if (event?.data?.id) {
    const resolvedRoute = router.resolve({ name: "order-detail", params: { orderId: event?.data?.order_id } });
    window.open(resolvedRoute.href, "_blank");
  }
};

const dropdownPageChange = (value) => {
  const newPageSkip = (value - 1) * pageLimit.value;
  if (pageSkip.value !== newPageSkip) {
    pageSkip.value = newPageSkip;
  }

  currentPage.value = value;
  currentFirst.value = newPageSkip;
};

const dataTablePageChange = (event) => {
  const newPageSkip = event.page * pageLimit.value;
  if (pageSkip.value !== newPageSkip) {
    pageSkip.value = newPageSkip;
  }
  if (event.rows !== pageLimit) {
    pageLimit.value = event.rows;
  }
  currentPage.value = event.page + 1;
  currentFirst.value = newPageSkip;
};

watch(
  status,
  (newStatus, oldStatus) => {
    if (newStatus && oldStatus && !newStatus.every((element, index) => element === oldStatus[index])) {
      refetch();
    }
    const statusValue = Array.isArray(newStatus) && newStatus.length === 1 ? newStatus[0] : undefined;
    switch (statusValue) {
      case PriceOverrideStatus.Rejected:
        setHiddenColumns(REJECTED_HIDDEN_COLUMNS);
        break;
      case PriceOverrideStatus.Approved:
        setHiddenColumns(APPROVED_HIDDEN_COLUMNS);
        break;
      case PriceOverrideStatus.NeedsReview:
        setHiddenColumns(NEEDS_REVIEW_HIDDEN_COLUMNS);
        break;
      case PriceOverrideStatus.Reverted:
        setHiddenColumns(REVERTED_HIDDEN_COLUMNS);
        break;
      default:
        setHiddenColumns(DEFAULT_HIDDEN_COLUMNS);
        break;
    }
  },
  { immediate: true },
);

watch(isRefetching, (newIsRefetching, oldIsRefetching) => {
  if (newIsRefetching) {
    selectedRows.value = [];
  }
});
</script>

<template>
  <div class="mt-6">
    <Toast position="bottom-right" group="br" />
    <PriceOverrideBulkUpdateModal :data="selectedRows" :modal-visible="showBulkEditModal" @hide="toggleShowBulkEditModal(false)"/>
    <div class="flex justify-between items-center h-8 my-4">
      <Button label="BULK EDIT" v-show="enableBulkEdit" :disabled="!enableBulkEdit" link @click="bulkEdit" :pt="priceOverrideTablePresets.bulkEditButton"/>
      <div class="align-content-end flex items-center ml-auto">
        <div class="mr-2">{{ priceOverridesResponse?.data?.count }} Items</div>
        <Dropdown :model-value="currentPage" @update:modelValue="dropdownPageChange" option-label="key" option-value="value" :options="pageDropDownOptions" />
        <div class="ml-2">of {{ Number.parseInt(`${pageCount}`, 10) }}</div>
        <Divider class="!m-3" layout="vertical" />
        <div class="">View</div>
        <Dropdown class="ml-2" v-model="pageLimit" option-label="key" option-value="value" :options="ROWS_PER_PAGE_DROPDOWN_OPTIONS" />
        <Button link @click="toggleColumnSelectorPanel">
          <span class="material-symbols-outlined">settings</span>
        </Button>
        <OverlayPanel ref="columnSelectorPanel">
          <div class="flex flex-col">
            <div v-for="columnOption of columnOptions" :key="columnOption.key" class="flex items-center mb-4">
              <Checkbox v-model="displayableColumns" :inputId="columnOption.key" :name="columnOption.key" :value="columnOption.key" :pt="filtersPresets.checkbox"/>
              <label :for="columnOption.key" class="ml-4">{{ columnOption.name }}</label>
            </div>
          </div>
        </OverlayPanel>
      </div>
    </div>
    <DataTable
      data-test-id="price-overrides-table"
      pv_id_6
      :value="priceOverridesResponse?.data.items || []"
      :pt="priceOverrideTablePresets.datatable"
      paginator
      :always-show-paginator="true"
      :rows="pageLimit"
      :totalRecords="priceOverridesResponse?.data?.count || 0"
      :rowsPerPageOptions="ROWS_PER_PAGE_OPTIONS"
      :loading="isLoading"
      v-model:selection="selectedRows"
      dataKey="id"
      resizableColumns
      columnResizeMode="fit"
      tableStyle="min-width: 50rem"
      table-class="w-50 min-h-40"
      :lazy="true"
      :first="currentFirst"
      @page="dataTablePageChange"
      @row-click="onRowClicked"
    >
      <Column selectionMode="multiple" column-key="select_box" headerStyle="width: 3rem" :hidden="!enableBulkEdit"></Column>
      <Column v-if="canDisplayColumn('id')" header="Id" field="id" column-key="id" header-class="p-resizable-column" class="w-[140px]"/>
      <Column v-if="canDisplayColumn('order_id')" column-key="order_id" field="order_id" header="Order Id" class="w-[120px]" />
      <Column v-if="canDisplayColumn('program_session_id')" column-key="program_session_id" field="program_session_id" header="Program Session Id" class="w-[120px]" />
      <Column header="Name and Email" column-key="participant_name" class="w-[260px]">
        <template #body="slotProps">
          <div>
            <div class="text-university-primary">{{ slotProps.data.participant_fullname }}</div>
            <div class="text-teal-900">{{ slotProps.data.participant_email }}</div>
          </div>
        </template>
      </Column>
      <Column v-if="canDisplayColumn('name')" header="Experience" field="name" column-key="name" header-class="p-resizable-column" class="w-[260px]"/>
      <Column v-if="canDisplayColumn('product_type')" header="Product Type" field="product_type" column-key="product_type" class="w-[180px]"/>
      <Column v-if="canDisplayColumn('override_amount_in_cents')" header="Requested Amount" field="override_amount_in_cents" column-key="override_amount_in_cents" class="w-[120px]" header-class="">
        <template #body="slotProps">
          <div>{{ slotProps.data?.override_amount_in_cents? formattedPriceFromCents(slotProps.data?.override_amount_in_cents) : "" }}</div>
        </template>
      </Column>
      <Column v-if="canDisplayColumn('original_amount_in_cents')" header="Original Amount" field="original_amount_in_cents" column-key="original_amount_in_cents" class="w-[120px]" header-class="">
        <template #body="slotProps">
          <div>{{ slotProps.data?.original_amount_in_cents? formattedPriceFromCents(slotProps.data?.original_amount_in_cents) : "" }}</div>
        </template>
      </Column>
      <Column v-if="canDisplayColumn('difference_amount_in_cents')" header="Difference Amount" field="" column-key="difference_amount_in_cents" class="w-[120px]" header-class="">
        <template #body="slotProps">
          <div>{{ formattedPriceFromCents(getPriceDifference(slotProps.data)) }}</div>
        </template>
      </Column>
      <Column v-if="canDisplayColumn('override_reason')" header="Reason" field="override_reason" column-key="override_reason" class="w-[260px]"/>
      <Column v-if="canDisplayColumn('decision_date')" column-key="decision_date" field="decision_date" header="Decision Date" class="w-[120px]">
        <template #body="slotProps">
          <div>{{ slotProps?.data?.decision_date ? formatDateUtc(slotProps?.data?.decision_date) : "" }}</div>
        </template>
      </Column>
      <Column v-if="canDisplayColumn('requested_by')" column-key="requested_by" field="requested_by" header="Requested By" class="w-[120px]" />
      <Column v-if="canDisplayColumn('request_notes')" column-key="request_notes" field="request_notes" header="Request Notes" class="w-[220px]" />
      <Column v-if="canDisplayColumn('decided_by')" column-key="decided_by" field="decided_by" header="Decided By" class="w-[120px]" />
      <Column v-if="canDisplayColumn('decision_notes')" column-key="decision_notes" field="decision_notes" header="Decision Notes" class="w-[220px]" />
      <Column v-if="canDisplayColumn('city_ascii')" column-key="city_ascii" field="city_ascii" header="City Name" class="w-[120px]" />
      <Column v-if="canDisplayColumn('view_details') && showViewDetails" column-key="view_details"  field="id" header="View Details" class="w-[120px]">
        <template #body="slotProps">
          <DetailModal :data="slotProps?.data" />
        </template>
      </Column>

      <Column v-if="canDisplayColumn('approved_date') && showApprovedDate" column-key="approved_date" field="decision_date" header="Approved Date" class="w-[120px]">
        <template #body="slotProps">
          <div>{{ slotProps?.data?.decision_date ? formatDateUtc(slotProps?.data?.decision_date) : "" }}</div>
        </template>
      </Column>
      <Column v-if="canDisplayColumn('requested_date') && showRequestedDate" field="requested_date" header="Requested Date" class="w-[120px]">
        <template #body="slotProps">
          <div>{{ slotProps?.data?.requested_date ? formatDateUtc(slotProps?.data?.requested_date) : "" }}</div>
        </template>
      </Column>
      <Column v-if="canDisplayColumn('rejected_date') && showRejectedDate" column-key="rejected_date" field="decision_date" header="Declined Date" class="min-w-[120px] ">
        <template #body="slotProps">
          <div>{{ slotProps?.data?.decision_date ? formatDateUtc(slotProps?.data?.decision_date) : "" }}</div>
        </template>
      </Column>
      <Column frozen align-frozen="right" field="status" header="Approval Status" column-key="rejected_date" class="w-[220px] override-approval-status" body-class="override-approval-status">
        <template #body="slotProps">
          <ApprovalStatus :data="slotProps.data"/>
        </template>
      </Column>
      <template #empty>
        No Results Found.
      </template>
    </DataTable>
  </div>
</template>

<style>
.override-approval-status {
  z-index: 1;
  overflow: visible;
}

.override-approval-status::before,
.override-approval-status::after {
  content: "";
  position: absolute;
  top: 0;
  height: 100%;
  width: 12px;
  pointer-events: none;
  z-index: -1;
}

.override-approval-status::before {
  left: 0;
  box-shadow: -10px 0 14px 0px rgba(0, 0, 0, 0.15)
}
.override-approval-status::after {
  right: 8px;
  box-shadow: 5px 0 8px -2px rgba(0, 0, 0, 0.15)
}

</style>
